<script>
import { ICONS } from '@modules/atendimento/constants/Text'
import { hexToRgb } from '@modules/global/utils/Color'

export default {
  name: 'SearchContactList',
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    selected: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    label: {
      type: String,
      default: 'Buscar contato',
    },
    placeholder: {
      type: String,
      default: 'Nome ou telefone',
    },
    noResultsText: {
      type: String,
      default: 'Nenhum contato encontrado',
    },
    displayField: {
      type: [String, Array],
      default: null,
    },
    subtitleField: {
      type: [String, Array],
      default: null,
    },
  },
  data() {
    return {
      icons: ICONS,
      searchQuery: '',
    }
  },
  computed: {
    hasResults() {
      return this.items.length > 0
    },
    hasSelected() {
      return !!this.selected
    },
    showMenu() {
      if (this.hasSelected) return false
      if (this.searchQuery.length < 2) return false
      if (this.loading) return true
      return this.hasResults || this.searchQuery.length >= 2
    },
    selectedBgColor() {
      const primary = getComputedStyle(document.documentElement)
        .getPropertyValue('--primary')
        .trim()
      if (!primary) return 'rgba(0, 0, 0, 0.08)'
      const [r, g, b] = hexToRgb(primary)
      return `rgba(${r}, ${g}, ${b}, 0.15)`
    },
  },
  watch: {
    selected(val) {
      if (val) {
        this.searchQuery = ''
      }
    },
  },
  methods: {
    onInput(value) {
      this.searchQuery = value
      this.$emit('search', value)
    },
    onSelect(item) {
      this.$emit('select', item)
    },
    onClear() {
      this.$emit('clear')
    },
    getInitial(item) {
      const name = this.getDisplayName(item)
      return (name || '?').charAt(0)
    },
    getDisplayName(item) {
      if (this.displayField) {
        return this.resolveField(item, this.displayField) || 'Desconhecido'
      }
      return item.nome || item.nome_contato || 'Desconhecido'
    },
    getSubtitle(item) {
      if (this.subtitleField) {
        return this.resolveField(item, this.subtitleField) || ''
      }
      return item.telefone || ''
    },
    getSecondaryName(item) {
      if (this.displayField) return null
      if (item.nome_contato && item.nome && item.nome_contato !== item.nome) {
        return item.nome_contato
      }
      return null
    },
    resolveField(item, field) {
      if (Array.isArray(field)) {
        for (const f of field) {
          if (item[f]) return item[f]
        }
        return null
      }
      return item[field] || null
    },
  },
}
</script>

<template>
  <div class="search-contact-list">
    <!-- Search input + floating menu -->
    <v-menu
      v-if="!hasSelected"
      :value="showMenu"
      offset-y
      :close-on-content-click="false"
      :close-on-click="false"
      :nudge-bottom="4"
      content-class="search-contact-list__menu"
      max-height="240"
    >
      <template v-slot:activator="{ on, attrs }">
        <div v-bind="attrs" v-on="on">
          <input-by-type
            :value="searchQuery"
            :labelExternal="label"
            :placeholder="placeholder"
            class="align-icon-search"
            dense
            outlined
            hide-details
            prepend-inner-icon="mdi-magnify"
            :loading="loading"
            @input="onInput"
          />
        </div>
      </template>

      <!-- Results list -->
      <v-list v-if="hasResults" class="pa-0">
        <v-list-item
          v-for="item in items"
          :key="item.id"
          class="search-contact-list__item"
          @click="onSelect(item)"
        >
          <slot name="item" :item="item">
            <v-avatar
              size="28"
              color="grey lighten-2"
              class="mr-4 flex-shrink-0"
            >
              <span class="az-text-small">{{ getInitial(item) }}</span>
            </v-avatar>
            <div class="flex-grow-1 overflow-hidden">
              <div class="az-text-primary az-size-14 font-medium text-truncate">
                {{ getDisplayName(item) }}
                <span
                  v-if="getSecondaryName(item)"
                  class="az-text-small az-muted ml-1"
                >
                  ({{ getSecondaryName(item) }})
                </span>
              </div>
              <div
                v-if="getSubtitle(item)"
                class="az-text-small az-muted text-truncate"
              >
                {{ getSubtitle(item) }}
              </div>
            </div>
          </slot>
        </v-list-item>
      </v-list>

      <!-- No results -->
      <div v-else class="d-flex justify-center align-center pa-4">
        <span class="az-text-small az-muted">{{ noResultsText }}</span>
      </div>
    </v-menu>

    <!-- Selected chip -->

    <div v-if="hasSelected">
      <div class="az-text-label">{{ label }}</div>
      <div
        class="search-contact-list__selected d-flex align-center"
        :style="{ backgroundColor: selectedBgColor }"
      >
        <slot name="selected" :item="selected" :onClear="onClear">
          <v-avatar size="28" color="primary" class="mr-2 flex-shrink-0">
            <span class="white--text" style="font-size: 12px">
              {{ getInitial(selected) }}
            </span>
          </v-avatar>
          <div class="flex-grow-1 overflow-hidden">
            <div class="az-text-primary az-size-14 font-medium text-truncate">
              {{ getDisplayName(selected) }}
            </div>
            <div
              v-if="getSubtitle(selected)"
              class="az-text-small az-muted text-truncate"
            >
              {{ getSubtitle(selected) }}
            </div>
          </div>
          <v-btn icon x-small class="mr-1" @click="onClear">
            <icon-svg :name="icons.close" size="14" color="#8E8E8E" />
          </v-btn>
        </slot>
      </div>
    </div>
  </div>
</template>

<style lang="sass" scoped>
.search-contact-list__selected
  border-radius: 999px
  padding: 6px 8px 6px 6px
  transition: background-color 0.2s ease
</style>

<style lang="sass">
.search-contact-list__menu
  border-radius: 12px !important
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.10) !important
  border: 1px solid #EEEEEE

  .v-list
    border-radius: 12px

  .search-contact-list__item
    min-height: 44px
    cursor: pointer
    transition: background-color 0.15s ease

  .search-contact-list__item:hover
    background-color: #F5F5F5

  .search-contact-list__item + .search-contact-list__item
    border-top: 1px solid #F0F0F0

  .search-contact-list__item.v-list-item--active::before,
  .search-contact-list__item.v-list-item--highlighted::before
    opacity: 0
</style>
